import { useDepot } from '#~/depot/hooks.js';
import { Slicer } from '#~/depot/slicer.js';
import type { Props as AddProps } from '#~/video/components/add/index.js';
import { Add } from '#~/video/components/add/index.js';
import type { Api } from '@studio/design-system/dialog';
import type { Room } from '@studio/models/room';
import { dequal } from 'dequal/lite';
import { pick } from 'lodash-es';
import { forwardRef, type ReactElement, createElement } from 'react';
import { create } from 'zustand';

export const useAddVideoStore = create<{
  show: boolean;
  setShow: (show: boolean) => void;
}>()((set) => ({
  show: false,
  setShow: (show) => set({ show })
}));

/**
 * A wrapper component around the Add video component that provides depot state.
 * Automatically injects agentIds, devices, discoveries and appId from the depot.
 */
const AddVideo = forwardRef<Api, Omit<AddProps, 'agentIds' | 'appId' | 'devices' | 'discoveries'>>(
  ({ roomId, ...props }, forwardedRef): ReactElement => {
    const passthru = useDepot(
      (µ) => ({
        ...pick(new Slicer(µ).room(roomId), ['agentIds', 'devices', 'discoveries']),
        appId: µ.appId
      }),
      dequal
    );

    return createElement(Add, { roomId, ...props, ...passthru, ref: forwardedRef });
  }
);

/** `AddVideoModal` component. */
export const AddVideoModal = ({ roomId }: { roomId: Room['id'] }) => {
  const { show, setShow } = useAddVideoStore();
  return show ? <AddVideo onOpenChange={setShow} roomId={roomId} /> : null;
};
